/**
 * FlashBook theme *design language*.
 *
 * `FlashBookThemeTokens` answers "what colours is this book?" — this module
 * answers "what does this book *look like*?", which is the part that decides
 * whether play mode reads as an interactive book or as a stack of app cards.
 *
 * A theme is described here by:
 *   page    — the paper: texture, ornament, sheet inset, radius
 *   block   — how content sits on the paper (open / ruled / panel / inset) and
 *             what divides one block from the next
 *   quiz    — the shape and marker of an answer row, and how a question is titled
 *   type    — the type scale, per role
 *   audio   — how the read-aloud control presents itself
 *   progress— the header's progress language
 *   motion  — the page-turn personality
 *
 * Every field is declarative data: no theme-specific components, and no branch
 * on theme id anywhere in the renderer. Adding a theme means writing one more
 * entry in `FLASHBOOK_THEME_DESIGNS`.
 *
 * Fonts are the one exception to "data only": a theme may be *written* rather
 * than set, and the classroom theme is. That is why `fontFamily` exists here at
 * all — and why the face it names is bundled with the app instead of asked of
 * the platform (see `#/themes-flashbook/hands`). Everything else carries
 * personality through weight, size, tracking, case and line-height, which render
 * identically everywhere without shipping a byte.
 */
import {type FlashBookDirection} from '#/lib/flashbookDirection'
import {type InkVoice} from '#/lib/inkVoice'
import {
  flashBookHand,
  flashBookHandIn,
  flashBookHandInkRatio,
  isFlashBookHand,
} from '#/themes-flashbook/hands'
import {
  ACADEMY_INK,
  COMIC_INK,
  FALLBACK_INK,
  HERITAGE_INK,
  LABORATORY_INK,
  MAGAZINE_INK,
  MATHEMATICS_INK,
  NOTEBOOK_INK,
  SPACE_ADVENTURE_INK,
  STORYBOOK_INK,
} from '#/themes-flashbook/inkVoices'

/**
 * Squared-paper square size, duplicated here from `FlashBookPageDecor` so the
 * design layer stays free of component imports (the decor module imports *this*
 * one, and a cycle would be the price of sharing a single constant).
 */
const GRID_STEP_FOR_DESIGN = 24

/**
 * The gap between a question's kicker and the question, unless a theme says
 * otherwise. Kept here so the renderer and the themes agree on one number.
 */
export const THEME_HEADING_GAP = 6

/** The classroom theme's ruling pitch, in px. */
const NOTEBOOK_STEP = 26

/** A whole number of ruled lines. */
const lines = (count: number) => count * NOTEBOOK_STEP

/**
 * Stamps the hand onto a role, so the writing is written.
 *
 * The family is chosen by the role's *own weight*: the bundled hands are one
 * family per weight, which is the naming that resolves on Android (by file
 * name), iOS (by internal family name) and the web (by stylesheet) alike. The
 * stack behind the family is platform-aware — see `#/themes-flashbook/hands`.
 */
const hand = (style: FlashBookTypeStyle): FlashBookTypeStyle => ({
  ...style,
  fontFamily: flashBookHand(style.fontWeight),
})

/**
 * The ink room a *clamped* heading needs, as padding the layout does not pay
 * for.
 *
 * `numberOfLines` clamps text by clipping it, and React Native clips at the box
 * edge — so a heading whose box is only as tall as its leading loses the bottom
 * of a descender. The bundled hands are exactly that case: 21px of script draws
 * 29px from the cap of a `b` to the tail of a `p`, and the classroom theme sets
 * that 21px inside the 26px rule so the writing closes on the ruled line.
 *
 * So the fix is padding, not leading. Padding sits *outside* the clamped lines:
 * the descenders get somewhere to land, and the baselines do not move. The
 * negative margin hands the box's own height straight back, which is what keeps
 * a ruled page ruled — growing the page heading by four pixels would push the
 * whole sheet, and every line written on it, off the rules underneath.
 *
 * Empty for anything that is not written in a hand: a set face's ink sits inside
 * its line box, which is what a line box is for.
 *
 * The ink measured against is the *face's own*: the Arabic hand draws a tenth of
 * an em taller than the Latin one (see `#/themes-flashbook/hands`), and both are
 * bundled, so a role that has been re-resolved for a right-to-left page asks for
 * the room that face needs rather than the room its Latin twin would.
 */
export function clampedInkRoom(style: FlashBookTypeStyle): {
  paddingBottom?: number
  marginBottom?: number
} {
  if (!style.fontFamily) return {}
  const pad = Math.max(
    0,
    Math.ceil(style.fontSize * flashBookHandInkRatio(style.fontFamily)) -
      Math.round(style.lineHeight),
  )
  return pad > 0 ? {paddingBottom: pad, marginBottom: -pad} : {}
}

/** A renderable subset of `TextStyle` — JSON-safe and theme-independent. */
export type FlashBookTypeStyle = {
  fontSize: number
  lineHeight: number
  /**
   * The face this role is written in.
   *
   * Set only for a theme that is *written* rather than set, through `hand(...)`,
   * and always to a family the app bundles — never to one asked of the platform.
   * See `#/themes-flashbook/hands` for what the stack resolves to off the web.
   */
  fontFamily?: string
  fontWeight: '400' | '500' | '600' | '700' | '800' | '900' | 'normal' | 'bold'
  letterSpacing?: number
  textTransform?: 'none' | 'uppercase'
  fontStyle?: 'normal' | 'italic'
  marginBottom?: number
}

/** The paper a page is printed on. */
export type FlashBookPageTexture =
  | 'plain'
  /** Ruled exercise-book lines with a margin rule. */
  | 'ruled'
  /** Squared / graph paper. */
  | 'grid'
  /** Warm paper: faint speckle and a deckled edge. */
  | 'paper'
  /** Starfield. */
  | 'stars'
  /** Cool editorial stock with a hairline column rule. */
  | 'column'

export type FlashBookPageOrnament =
  | 'none'
  | 'chapter-rule'
  | 'stars'
  | 'grid'
  | 'ribbon'
  | 'dots'

/**
 * How a block presents itself on the page.
 *
 * `open`  — flows directly on the paper (reading, not widgets)
 * `ruled` — sits above a rule, the way an exercise is filled in
 * `panel` — gets its own surface (focused, app-like themes)
 * `inset` — a tinted well inside the page
 */
export type FlashBookBlockMode = 'open' | 'ruled' | 'panel' | 'inset'

export type FlashBookBlockDivider =
  | 'none'
  | 'hairline'
  | 'double-rule'
  | 'center-dot'
  | 'ornament'

export type FlashBookOptionShape =
  /** Bordered card — focused / app-like. */
  | 'card'
  /** Full-width row on the paper, separated by rules. */
  | 'row'
  /** Rounded, tinted, no border — soft and friendly. */
  | 'soft'
  /** Editorial slab: square, heavy left edge. */
  | 'slab'

export type FlashBookOptionMarker = 'radio' | 'letter' | 'box' | 'dash' | 'none'

/** How a question announces itself. */
export type FlashBookQuestionStyle =
  | 'plain'
  /** Small uppercase kicker above the question. */
  | 'eyebrow'
  /** "Question 3" in an accent tab. */
  | 'tab'
  /** Heavy, book-like, with a rule under it. */
  | 'heading'

/** How a quotation is set off from the prose around it. */
export type FlashBookQuoteStyle =
  /** Ornamented rules above and below, book-like. */
  | 'flourish'
  /** Printed in the margin, off a red exercise rule. */
  | 'margin'
  /** A heavy left bar beside the text. */
  | 'bar'
  /** Editorial pull quote: oversized quote mark over a rule. */
  | 'pull'
  /** [ Bracketed ] like a mission log entry. */
  | 'bracket'
  /** A tinted, framed plate — archival. */
  | 'plate'

/** How a callout / note block is contained. */
export type FlashBookCalloutStyle =
  /** Soft tinted panel, no border. */
  | 'tint'
  /** Hairline outline box. */
  | 'outline'
  /** Heavy left rule with a kicker label. */
  | 'sidebar'
  /** Icon badge then text, no container at all. */
  | 'badge'
  /** A label "tape" pinned to the top edge. */
  | 'tape'

/** How an image caption is set. */
export type FlashBookCaptionStyle =
  | 'small-caps'
  | 'italic'
  /** Uppercase kicker with a leading rule. */
  | 'kicker'
  /** Quiet low-contrast line. */
  | 'soft'

/** How a table is ruled. */
export type FlashBookTableStyle =
  /** Header rule + row rules (exercise book). */
  | 'ruled'
  /** Header band + alternating row tint. */
  | 'zebra'
  /** Full outline, no inner row rules. */
  | 'outline'
  /** Header rule only — the most editorial. */
  | 'open'
  /** Squared paper: every cell bordered. */
  | 'grid'

/** How a formula is presented. */
export type FlashBookFormulaStyle =
  /** A raised plate the formula is printed on. */
  | 'plate'
  /** A hairline box. */
  | 'boxed'
  /** Handwritten on the ruled page — no container. */
  | 'chalk'
  /** Set inline in the prose flow. */
  | 'inline'

export type FlashBookDesign = {
  page: {
    texture: FlashBookPageTexture
    ornament: FlashBookPageOrnament
    /** Padding inside the page sheet, per side. */
    padding: number
    /** Corner radius of the page sheet. */
    radius: number
    /** Gap between the page edge and the sheet (book-like inset). */
    sheetInset: number
    /**
     * Pitch of the theme's ruling, in px.
     *
     * Defaults to the body line height, which is what makes an exercise-book
     * page read as *lined paper the text is written on* rather than a grid laid
     * behind the glyphs. Square paper overrides it to keep its squares square.
     */
    textureStep?: number
  }
  block: {
    mode: FlashBookBlockMode
    /** Vertical rhythm between blocks. */
    gap: number
    divider: FlashBookBlockDivider
    /** Padding inside a `panel` / `inset` block. */
    padding: number
    radius: number
    borderWidth: number
    /** Opacity of rules, dividers and hairlines (paper ink is never pure). */
    ruleOpacity: number
  }
  quiz: {
    option: FlashBookOptionShape
    optionRadius: number
    optionBorderWidth: number
    optionPadding: number
    marker: FlashBookOptionMarker
    question: FlashBookQuestionStyle
    /**
     * Space between a question's kicker and the question itself.
     *
     * The default (`THEME_HEADING_GAP`) reads well on paper that is not ruled.
     * A ruled theme sets it to 0 so the kicker and the question sit on adjacent
     * lines — anything in between is a gap the writing floats across.
     */
    headingGap?: number
    /**
     * How a chosen answer is marked.
     *
     * `tint` (the default) fills the row — a soft surface, which is right for a
     * screen but reads as a UI band on paper. `highlight` puts a highlighter
     * stroke *over the words themselves* instead, the way a learner marks their
     * own book: no box, no border, just ink on top of the writing.
     */
    selection?: 'tint' | 'highlight'
    /**
     * The marker's ink for a chosen-but-unjudged answer, when `selection` is
     * `highlight`. Correct and wrong answers use the palette's success and error
     * inks, so the stroke always agrees with the tick beside it.
     */
    highlightInk?: string
  }
  type: {
    /** Cover title. */
    display: FlashBookTypeStyle
    /** Page / section title. */
    title: FlashBookTypeStyle
    /** The question itself. */
    question: FlashBookTypeStyle
    /** Reading text. */
    body: FlashBookTypeStyle
    /** Answer labels and control text. */
    label: FlashBookTypeStyle
    /** Page numbers, kickers, captions. */
    caption: FlashBookTypeStyle
  }
  audio: {
    style: 'pill' | 'plain' | 'badge'
    /** Which translated label the read-aloud control uses. */
    label: 'read-to-me' | 'listen' | 'play-reading'
  }
  /**
   * What this theme's answer sounds are made of.
   *
   * Declarative data rather than a theme id the sound engine looks up, for the same
   * reason as everything else in this type: the reader, the studio's preview and the
   * theme selector all hold a *design*, and none of them holds a theme id. It also
   * makes "each theme has its own feedback" a fact about the data — see the test that
   * holds all nine to it.
   */
  sound: InkVoice
  progress: {
    style: 'bar' | 'dots' | 'rule'
  }
  /**
   * How the *rich reading blocks* present themselves: quotations, notes,
   * captions, tables and formulas. These are the blocks that a generic card
   * wrapper destroys — a pull quote is not a card, and a formula on squared
   * paper should be written on the paper. Same content, different world.
   */
  presentation: {
    quote: FlashBookQuoteStyle
    callout: FlashBookCalloutStyle
    caption: FlashBookCaptionStyle
    table: FlashBookTableStyle
    formula: FlashBookFormulaStyle
  }
  motion: 'page' | 'ink' | 'orbit' | 'minimal' | 'none'
}

/** Themes that predate the design layer, and unknown/HTML themes. */
export const DEFAULT_FLASHBOOK_DESIGN: FlashBookDesign = {
  page: {
    texture: 'plain',
    ornament: 'none',
    padding: 20,
    radius: 0,
    sheetInset: 0,
  },
  block: {
    mode: 'open',
    gap: 18,
    divider: 'hairline',
    padding: 0,
    radius: 12,
    borderWidth: 0,
    ruleOpacity: 0.5,
  },
  quiz: {
    option: 'soft',
    optionRadius: 12,
    optionBorderWidth: 0,
    optionPadding: 14,
    marker: 'radio',
    question: 'plain',
  },
  type: {
    display: {
      fontSize: 30,
      lineHeight: 36,
      fontWeight: '900',
      letterSpacing: -0.4,
    },
    title: {fontSize: 21, lineHeight: 28, fontWeight: '800', marginBottom: 8},
    question: {fontSize: 19, lineHeight: 27, fontWeight: '700'},
    body: {fontSize: 16, lineHeight: 26, fontWeight: '400'},
    label: {fontSize: 15, lineHeight: 21, fontWeight: '500'},
    caption: {
      fontSize: 11,
      lineHeight: 16,
      fontWeight: '700',
      letterSpacing: 0.6,
    },
  },
  audio: {style: 'pill', label: 'read-to-me'},
  sound: FALLBACK_INK,
  progress: {style: 'bar'},
  presentation: {
    quote: 'bar',
    callout: 'tint',
    caption: 'soft',
    table: 'open',
    formula: 'boxed',
  },
  motion: 'minimal',
}

/**
 * Storybook — warm paper, cosy reading.
 *
 * The most "printed" of the themes: an open page, a flourished rule between
 * blocks, lettered answer rows and a chapter-style question heading. Nothing
 * here is a bordered app card.
 */
const STORYBOOK: FlashBookDesign = {
  page: {
    texture: 'paper',
    ornament: 'chapter-rule',
    padding: 22,
    radius: 6,
    sheetInset: 10,
  },
  block: {
    mode: 'open',
    gap: 24,
    divider: 'ornament',
    padding: 0,
    radius: 8,
    borderWidth: 0,
    ruleOpacity: 0.55,
  },
  quiz: {
    option: 'soft',
    optionRadius: 8,
    optionBorderWidth: 0,
    optionPadding: 14,
    marker: 'letter',
    question: 'heading',
  },
  type: {
    display: {
      fontSize: 31,
      lineHeight: 37,
      fontWeight: '800',
      letterSpacing: -0.3,
    },
    title: {fontSize: 22, lineHeight: 29, fontWeight: '800', marginBottom: 4},
    question: {fontSize: 20, lineHeight: 30, fontWeight: '700'},
    body: {fontSize: 17, lineHeight: 29, fontWeight: '400'},
    label: {fontSize: 16, lineHeight: 22, fontWeight: '500'},
    caption: {
      fontSize: 11,
      lineHeight: 16,
      fontWeight: '700',
      letterSpacing: 1,
    },
  },
  audio: {style: 'plain', label: 'read-to-me'},
  sound: STORYBOOK_INK,
  progress: {style: 'bar'},
  presentation: {
    quote: 'flourish',
    callout: 'tape',
    caption: 'italic',
    table: 'open',
    formula: 'plate',
  },
  motion: 'page',
}

/**
 * Notebook — ruled exercise paper.
 *
 * Every block is an exercise written on the ruled sheet: a rule above, a margin
 * number beside, lettered options in a circle. The blue ink accent is the
 * teacher's pen.
 */
const NOTEBOOK: FlashBookDesign = {
  page: {
    texture: 'ruled',
    ornament: 'grid',
    padding: 20,
    radius: 3,
    sheetInset: 8,
    // One rule per body text line — the writing sits on the lines.
    textureStep: NOTEBOOK_STEP,
  },
  block: {
    mode: 'ruled',
    gap: 22,
    divider: 'hairline',
    padding: 0,
    radius: 4,
    borderWidth: 0,
    ruleOpacity: 0.7,
  },
  quiz: {
    option: 'row',
    optionRadius: 6,
    optionBorderWidth: 1,
    optionPadding: 12,
    marker: 'letter',
    question: 'eyebrow',
    // The kicker sits on the rule above the question, not floating between two.
    headingGap: 0,
    // A classroom answer is marked with a highlighter, not selected in a form.
    selection: 'highlight',
    highlightInk: '#f0c04a',
  },
  /**
   * Every role that prints *inside* the ruled content is exactly one rule tall,
   * so each line of writing closes on a line of the paper.
   *
   * `display` is the exception and deliberately so: it sets the cover and the
   * report, which are not printed on the ruling, and squeezing a 29px cover
   * title into one 26px rule would be the tail wagging the dog.
   */
  type: {
    display: {
      fontSize: 29,
      lineHeight: 35,
      fontWeight: '800',
      letterSpacing: -0.2,
    },
    title: hand({fontSize: 21, lineHeight: lines(1), fontWeight: '800'}),
    question: hand({fontSize: 19, lineHeight: lines(1), fontWeight: '700'}),
    body: hand({fontSize: 16, lineHeight: lines(1), fontWeight: '400'}),
    label: hand({fontSize: 15, lineHeight: lines(1), fontWeight: '500'}),
    caption: hand({
      fontSize: 10,
      lineHeight: lines(1),
      fontWeight: '800',
      letterSpacing: 1.2,
    }),
  },
  audio: {style: 'pill', label: 'listen'},
  sound: NOTEBOOK_INK,
  progress: {style: 'rule'},
  presentation: {
    quote: 'margin',
    callout: 'sidebar',
    caption: 'kicker',
    table: 'ruled',
    formula: 'chalk',
  },
  motion: 'ink',
}

/**
 * Academy — focused study.
 *
 * Structured and restrained: real panels with a hairline border, radio options,
 * minimal motion. This is the only theme where a bordered surface is the point.
 */
const ACADEMY: FlashBookDesign = {
  page: {
    texture: 'plain',
    ornament: 'none',
    padding: 18,
    radius: 16,
    sheetInset: 0,
  },
  block: {
    mode: 'panel',
    gap: 14,
    divider: 'none',
    padding: 16,
    radius: 14,
    borderWidth: 1,
    ruleOpacity: 0.6,
  },
  quiz: {
    option: 'card',
    optionRadius: 12,
    optionBorderWidth: 1,
    optionPadding: 15,
    marker: 'radio',
    question: 'eyebrow',
  },
  type: {
    display: {
      fontSize: 29,
      lineHeight: 35,
      fontWeight: '800',
      letterSpacing: -0.5,
    },
    title: {fontSize: 20, lineHeight: 27, fontWeight: '700', marginBottom: 4},
    question: {fontSize: 18, lineHeight: 26, fontWeight: '700'},
    body: {fontSize: 15.5, lineHeight: 25, fontWeight: '400'},
    label: {fontSize: 15, lineHeight: 21, fontWeight: '500'},
    caption: {
      fontSize: 10,
      lineHeight: 15,
      fontWeight: '700',
      letterSpacing: 1.1,
    },
  },
  audio: {style: 'pill', label: 'play-reading'},
  sound: ACADEMY_INK,
  progress: {style: 'bar'},
  presentation: {
    quote: 'bar',
    callout: 'outline',
    caption: 'soft',
    table: 'zebra',
    formula: 'boxed',
  },
  motion: 'minimal',
}

/**
 * Magazine — editorial.
 *
 * Open columns, numbered answers set as slabs with a heavy left edge, a double
 * rule between sections and a masthead ornament. No rounded app surfaces at all.
 */
const MAGAZINE: FlashBookDesign = {
  page: {
    texture: 'column',
    ornament: 'ribbon',
    padding: 22,
    radius: 2,
    sheetInset: 0,
  },
  block: {
    mode: 'open',
    gap: 26,
    divider: 'double-rule',
    padding: 0,
    radius: 2,
    borderWidth: 0,
    ruleOpacity: 0.6,
  },
  quiz: {
    option: 'slab',
    optionRadius: 2,
    optionBorderWidth: 0,
    optionPadding: 16,
    marker: 'letter',
    question: 'eyebrow',
  },
  type: {
    display: {
      fontSize: 33,
      lineHeight: 38,
      fontWeight: '900',
      letterSpacing: -0.8,
    },
    title: {
      fontSize: 24,
      lineHeight: 30,
      fontWeight: '900',
      letterSpacing: -0.4,
      marginBottom: 4,
    },
    question: {
      fontSize: 20,
      lineHeight: 28,
      fontWeight: '800',
      letterSpacing: -0.2,
    },
    body: {fontSize: 16, lineHeight: 27, fontWeight: '400'},
    label: {fontSize: 15.5, lineHeight: 22, fontWeight: '500'},
    caption: {
      fontSize: 10,
      lineHeight: 15,
      fontWeight: '800',
      letterSpacing: 1.4,
    },
  },
  audio: {style: 'badge', label: 'listen'},
  sound: MAGAZINE_INK,
  progress: {style: 'rule'},
  presentation: {
    quote: 'pull',
    callout: 'sidebar',
    caption: 'kicker',
    table: 'outline',
    formula: 'inline',
  },
  motion: 'none',
}

/**
 * Space Adventure — cosmic mission.
 *
 * The page is a starfield console: panels with a soft glow, dashed answer
 * markers, a mission-briefing ornament and orbital motion.
 */
const SPACE_ADVENTURE: FlashBookDesign = {
  page: {
    texture: 'stars',
    // No ornament: this theme's progress language is *already* a row of mission
    // dots in the running head, and a second dot row directly beneath it read as
    // a duplicated progress bar rather than a page mark. The starfield carries
    // the identity.
    ornament: 'none',
    padding: 20,
    radius: 14,
    sheetInset: 8,
  },
  block: {
    mode: 'panel',
    gap: 16,
    divider: 'center-dot',
    padding: 16,
    radius: 12,
    borderWidth: 1,
    ruleOpacity: 0.55,
  },
  quiz: {
    option: 'card',
    optionRadius: 12,
    optionBorderWidth: 1,
    optionPadding: 15,
    marker: 'dash',
    question: 'tab',
  },
  type: {
    display: {
      fontSize: 30,
      lineHeight: 36,
      fontWeight: '900',
      letterSpacing: -0.3,
    },
    title: {fontSize: 20, lineHeight: 27, fontWeight: '700', marginBottom: 4},
    question: {fontSize: 18.5, lineHeight: 26, fontWeight: '700'},
    body: {fontSize: 15.5, lineHeight: 25, fontWeight: '400'},
    label: {fontSize: 15, lineHeight: 21, fontWeight: '500'},
    caption: {
      fontSize: 10,
      lineHeight: 15,
      fontWeight: '800',
      letterSpacing: 1.6,
    },
  },
  audio: {style: 'badge', label: 'listen'},
  sound: SPACE_ADVENTURE_INK,
  progress: {style: 'dots'},
  presentation: {
    quote: 'bracket',
    callout: 'badge',
    caption: 'kicker',
    table: 'grid',
    formula: 'plate',
  },
  motion: 'orbit',
}

/**
 * Laboratory — a science notebook crossed with an instrument panel.
 *
 * Measurement paper, bounded panels the way a lab sheet boxes each observation,
 * squared tables (data belongs in cells), boxed formulas and a tab-style
 * question header. Tighter leading than the reading themes: a method list is
 * scanned, not read aloud.
 */
const LABORATORY: FlashBookDesign = {
  page: {
    texture: 'grid',
    ornament: 'grid',
    padding: 18,
    radius: 10,
    sheetInset: 6,
    // Squares stay square *and* sit on the text: the ruling's pitch is the
    // body line height. See the note on `textureStep`.
    textureStep: GRID_STEP_FOR_DESIGN,
  },
  block: {
    mode: 'panel',
    gap: 14,
    divider: 'hairline',
    padding: 15,
    radius: 10,
    borderWidth: 1,
    ruleOpacity: 0.6,
  },
  quiz: {
    option: 'card',
    optionRadius: 8,
    optionBorderWidth: 1,
    optionPadding: 14,
    marker: 'box',
    question: 'tab',
    // Half a square of kicker plus half a square of air: the tab and the
    // question together fill exactly one square, so the question's first line
    // lands on a horizontal rule of the measurement paper.
    headingGap: GRID_STEP_FOR_DESIGN / 2,
  },
  type: {
    display: {
      fontSize: 29,
      lineHeight: 34,
      fontWeight: '800',
      letterSpacing: -0.3,
    },
    title: {fontSize: 20, lineHeight: 24, fontWeight: '700'},
    question: {fontSize: 18, lineHeight: 24, fontWeight: '700'},
    // One body line == one square of the measurement paper, so the horizontal
    // rules close each line of text instead of drifting through it.
    body: {fontSize: 15.5, lineHeight: 24, fontWeight: '400'},
    label: {fontSize: 14.5, lineHeight: 24, fontWeight: '600'},
    caption: {
      fontSize: 10,
      lineHeight: GRID_STEP_FOR_DESIGN / 2,
      fontWeight: '800',
      letterSpacing: 1.5,
    },
  },
  audio: {style: 'badge', label: 'listen'},
  sound: LABORATORY_INK,
  progress: {style: 'bar'},
  presentation: {
    quote: 'bar',
    callout: 'outline',
    caption: 'kicker',
    table: 'grid',
    formula: 'boxed',
  },
  motion: 'minimal',
}

/**
 * Mathematics — squared paper and chalk work.
 *
 * The ruling *is* the subject: graph paper, numbered exercise rows, formulas
 * written straight onto the squares instead of sitting in a widget, and tables
 * ruled like a worked solution. Nothing is decorated; the notation is the
 * ornament.
 */
const MATHEMATICS: FlashBookDesign = {
  page: {
    texture: 'grid',
    ornament: 'none',
    padding: 20,
    radius: 4,
    sheetInset: 8,
    // Squares stay square regardless of the body leading.
    textureStep: GRID_STEP_FOR_DESIGN,
  },
  block: {
    mode: 'ruled',
    gap: 20,
    divider: 'hairline',
    padding: 0,
    radius: 4,
    borderWidth: 0,
    ruleOpacity: 0.5,
  },
  quiz: {
    option: 'row',
    optionRadius: 4,
    optionBorderWidth: 0,
    optionPadding: 13,
    marker: 'letter',
    question: 'eyebrow',
    // Half a square of kicker plus half a square of air, so the numbered
    // exercise starts on a square rather than three pixels off one.
    headingGap: GRID_STEP_FOR_DESIGN / 2,
  },
  type: {
    display: {
      fontSize: 29,
      lineHeight: 35,
      fontWeight: '800',
      letterSpacing: -0.4,
    },
    title: {fontSize: 20, lineHeight: 24, fontWeight: '800'},
    question: {fontSize: 18.5, lineHeight: 24, fontWeight: '700'},
    // 24 = one square of the graph paper, so a line of working sits exactly on
    // the grid rather than drifting across it.
    body: {fontSize: 16, lineHeight: 24, fontWeight: '400'},
    label: {fontSize: 15, lineHeight: 24, fontWeight: '500'},
    caption: {
      fontSize: 10,
      lineHeight: GRID_STEP_FOR_DESIGN / 2,
      fontWeight: '800',
      letterSpacing: 1.3,
    },
  },
  audio: {style: 'plain', label: 'read-to-me'},
  sound: MATHEMATICS_INK,
  progress: {style: 'bar'},
  presentation: {
    quote: 'bar',
    callout: 'outline',
    caption: 'kicker',
    table: 'ruled',
    formula: 'chalk',
  },
  motion: 'ink',
}

/**
 * Comic — bold panels and speech.
 *
 * Heavy inked panel borders, big soft answer bubbles, lettered markers and
 * dialogue-style callouts. The panel edge is the visual signature, so this is
 * one of the two themes where a block genuinely is a bordered thing — but the
 * border is comic ink, not an app card.
 */
const COMIC: FlashBookDesign = {
  page: {
    texture: 'plain',
    ornament: 'dots',
    padding: 16,
    radius: 8,
    sheetInset: 0,
  },
  block: {
    mode: 'panel',
    gap: 18,
    divider: 'none',
    padding: 14,
    radius: 14,
    borderWidth: 2,
    ruleOpacity: 0.85,
  },
  quiz: {
    option: 'soft',
    optionRadius: 18,
    optionBorderWidth: 2,
    optionPadding: 16,
    marker: 'letter',
    question: 'heading',
  },
  type: {
    display: {
      fontSize: 32,
      lineHeight: 37,
      fontWeight: '900',
      letterSpacing: -0.5,
    },
    title: {
      fontSize: 22,
      lineHeight: 28,
      fontWeight: '900',
      letterSpacing: -0.2,
      marginBottom: 4,
    },
    question: {fontSize: 20, lineHeight: 27, fontWeight: '800'},
    body: {fontSize: 16.5, lineHeight: 25, fontWeight: '500'},
    label: {fontSize: 16, lineHeight: 22, fontWeight: '600'},
    caption: {
      fontSize: 10,
      lineHeight: 15,
      fontWeight: '900',
      letterSpacing: 1.4,
      textTransform: 'uppercase',
    },
  },
  audio: {style: 'pill', label: 'read-to-me'},
  sound: COMIC_INK,
  progress: {style: 'dots'},
  presentation: {
    quote: 'bracket',
    callout: 'tape',
    caption: 'kicker',
    table: 'outline',
    formula: 'boxed',
  },
  motion: 'page',
}

/**
 * Heritage — an archival manuscript.
 *
 * Parchment, a flourished chapter rule, generous leading, plates for quotations
 * and formulas, and small-caps captions with wide tracking. Nothing is boxed
 * unless it is a *plate*, and even then the frame is thin and aged rather than
 * an app panel.
 */
const HERITAGE: FlashBookDesign = {
  page: {
    texture: 'paper',
    ornament: 'chapter-rule',
    padding: 24,
    radius: 4,
    sheetInset: 12,
  },
  block: {
    mode: 'open',
    gap: 26,
    divider: 'ornament',
    padding: 0,
    radius: 6,
    borderWidth: 0,
    ruleOpacity: 0.5,
  },
  quiz: {
    option: 'soft',
    optionRadius: 6,
    optionBorderWidth: 0,
    optionPadding: 15,
    marker: 'letter',
    question: 'heading',
  },
  type: {
    display: {
      fontSize: 32,
      lineHeight: 39,
      fontWeight: '800',
      letterSpacing: -0.2,
    },
    title: {fontSize: 23, lineHeight: 31, fontWeight: '800', marginBottom: 6},
    question: {fontSize: 20, lineHeight: 31, fontWeight: '700'},
    body: {fontSize: 17, lineHeight: 30, fontWeight: '400'},
    label: {fontSize: 16, lineHeight: 23, fontWeight: '500'},
    caption: {
      fontSize: 11,
      lineHeight: 17,
      fontWeight: '700',
      letterSpacing: 1.8,
      textTransform: 'uppercase',
    },
  },
  audio: {style: 'plain', label: 'listen'},
  sound: HERITAGE_INK,
  progress: {style: 'rule'},
  presentation: {
    quote: 'plate',
    callout: 'outline',
    caption: 'small-caps',
    table: 'ruled',
    formula: 'plate',
  },
  motion: 'page',
}

export const FLASHBOOK_THEME_DESIGNS: Record<string, FlashBookDesign> = {
  storybook: STORYBOOK,
  notebook: NOTEBOOK,
  academy: ACADEMY,
  magazine: MAGAZINE,
  'space-adventure': SPACE_ADVENTURE,
  laboratory: LABORATORY,
  mathematics: MATHEMATICS,
  comic: COMIC,
  heritage: HERITAGE,
}

/** The design for a theme id, falling back to the neutral default. */
export function getFlashbookDesign(themeId: string): FlashBookDesign {
  return FLASHBOOK_THEME_DESIGNS[themeId] ?? DEFAULT_FLASHBOOK_DESIGN
}

/**
 * The same design, with every *written* role in the face for this writing.
 *
 * A theme names one hand per role, and that name is a single string — which is
 * the only naming that resolves on a device. But a device resolves a family
 * without falling back per glyph, so a book read in Arabic needs the *Arabic*
 * hand named on the role rather than the Latin one with a hope: the bundled
 * Latin faces carry no Arabic at all, and an Arabic sentence handed one is
 * silently drawn in the system font.
 *
 * Only roles that actually name a bundled hand are touched. A theme that sets
 * its type is left exactly as it is — its `fontFamily` is undefined, so there is
 * nothing to re-resolve and no way for this to put a hand on a theme that has
 * none. Set roles and colour and metrics are untouched too: a direction changes
 * the alphabet, never the type scale.
 *
 * The web is a no-op on purpose, and provably so rather than by a branch here: a
 * browser resolves a family list per glyph, so the role's own stack already
 * covers both scripts, and the family this returns for it is the identical
 * string (see `flashBookHandIn`). What is left over is the answer itself — when
 * nothing changed, the *same object* comes back, so a page that needs no swap
 * costs no re-render anywhere it is compared.
 */
export function designInHand(
  design: FlashBookDesign,
  direction: FlashBookDirection | undefined,
): FlashBookDesign {
  const roles = Object.keys(design.type) as (keyof FlashBookDesign['type'])[]
  let type: FlashBookDesign['type'] | null = null

  for (const role of roles) {
    const style = design.type[role]
    if (!isFlashBookHand(style.fontFamily)) continue
    const face = flashBookHandIn(direction, style.fontWeight)
    if (face === style.fontFamily) continue
    type ??= {...design.type}
    type[role] = {...style, fontFamily: face}
  }

  return type ? {...design, type} : design
}
